import React from "react";
import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useDebounce } from "@/hooks/useDebounce";
import { IdeaCardSkeleton } from "@/components/skeletons/IdeaCardSkeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Search, Pencil, Trash2, ExternalLink, Loader2, Save, X, Sparkles, BarChart, Globe, GlobeLock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { z } from "zod";
import { AIFeedbackCard } from "./AIFeedbackCard";
import { ActionPlanSection } from "./ActionPlanSection";
import { GenerateDeckDialog } from "./GenerateDeckDialog";

interface AIFeedback {
  strengths: string;
  weaknesses: string;
  suggestions: string;
}

interface Idea {
  id: string;
  title: string;
  description: string | null;
  source_url: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
  score: number;
  is_public?: boolean;
  public_slug?: string | null;
  tagline?: string | null;
  ai_feedback: AIFeedback | null;
}

interface IdeaListProps {
  ideas: Idea[];
  loading: boolean;
  onIdeaUpdated: () => void;
  onIdeaDeleted: () => void;
  onIdeaSelect?: (ideaId: string) => void;
  selectedIdeaId?: string | null;
  userId: string;
}

const ITEMS_PER_PAGE = 6;

const updateSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(2000).optional(),
  source_url: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  tags: z.string().max(500).optional(),
});

export const IdeaList = ({ ideas, loading, onIdeaUpdated, onIdeaDeleted, onIdeaSelect, selectedIdeaId, userId }: IdeaListProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 400); // Debounce search with 400ms delay
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [feedbackDialogIdeaId, setFeedbackDialogIdeaId] = useState<string | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    source_url: "",
    tags: "",
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const selectedIdea = useMemo(
    () => ideas.find((idea) => idea.id === feedbackDialogIdeaId),
    [ideas, feedbackDialogIdeaId]
  );

  // Filter ideas based on debounced search query
  const filteredIdeas = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return ideas;

    const query = debouncedSearchQuery.toLowerCase();
    console.log(`[Search] Filtering ${ideas.length} ideas with query: "${query}"`);
    return ideas.filter((idea) => {
      const titleMatch = idea.title.toLowerCase().includes(query);
      const descMatch = idea.description?.toLowerCase().includes(query);
      const tagsMatch = idea.tags.some((tag) => tag.toLowerCase().includes(query));
      return titleMatch || descMatch || tagsMatch;
    });
  }, [ideas, debouncedSearchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredIdeas.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedIdeas = filteredIdeas.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when debounced search changes
  const resetPage = () => setCurrentPage(1);
  
  useMemo(() => {
    resetPage();
  }, [debouncedSearchQuery]);
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const startEdit = (idea: Idea) => {
    setEditingId(idea.id);
    setEditForm({
      title: idea.title,
      description: idea.description || "",
      source_url: idea.source_url || "",
      tags: idea.tags.join(", "),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", description: "", source_url: "", tags: "" });
  };

  const handleUpdate = async (id: string) => {
    const result = updateSchema.safeParse(editForm);

    if (!result.success) {
      toast({
        title: "Validation Error",
        description: result.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }

    setUpdating(true);

    try {
      const tagsArray = editForm.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const { error } = await supabase
        .from("ideas")
        .update({
          title: editForm.title.trim(),
          description: editForm.description.trim() || null,
          source_url: editForm.source_url.trim() || null,
          tags: tagsArray,
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Idea updated successfully",
      });

      setEditingId(null);
      onIdeaUpdated();
    } catch (error) {
      console.error("Error updating idea:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update idea",
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleting(true);

    try {
      const { error } = await supabase.from("ideas").delete().eq("id", deleteId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Idea deleted successfully",
      });

      setDeleteId(null);
      onIdeaDeleted();
    } catch (error) {
      console.error("Error deleting idea:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete idea",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePublic = async (idea: Idea) => {
    try {
      setTogglingPublic(idea.id);
      const newIsPublic = !idea.is_public;

      const { error: updateError } = await supabase
        .from("ideas")
        .update({ is_public: newIsPublic })
        .eq("id", idea.id);

      if (updateError) throw updateError;

      if (newIsPublic && !idea.tagline) {
        try {
          await supabase.functions.invoke("generate-tagline", {
            body: { ideaId: idea.id },
          });
        } catch (err) {
          console.error("Failed to generate tagline:", err);
        }
      }

      const { data: updatedIdea } = await supabase
        .from("ideas")
        .select("public_slug")
        .eq("id", idea.id)
        .single();

      if (newIsPublic && updatedIdea?.public_slug) {
        const publicUrl = `${window.location.origin}/i/${updatedIdea.public_slug}`;
        toast({
          title: "Idea is now public!",
          description: publicUrl,
          action: (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(publicUrl);
                toast({ title: "Link copied!" });
              }}
            >
              Copy Link
            </Button>
          ),
        });
      } else {
        toast({
          title: newIsPublic ? "Idea published" : "Idea is now private",
          description: newIsPublic ? "Your idea is now visible to everyone" : "Your idea is now hidden",
        });
      }

      onIdeaUpdated();
    } catch (err: any) {
      console.error("Error toggling public status:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update idea",
        variant: "destructive",
      });
    } finally {
      setTogglingPublic(null);
    }
  };

  const handleGetAIFeedback = async (idea: Idea) => {
    // If feedback already exists, just open the dialog
    if (idea.ai_feedback) {
      setFeedbackDialogIdeaId(idea.id);
      return;
    }

    setLoadingFeedback(true);
    setFeedbackDialogIdeaId(idea.id);

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData.user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("ai-feedback", {
        body: {
          ideaId: idea.id,
          title: idea.title,
          description: idea.description || "",
        },
      });

      if (error) throw error;

      // Log progress
      await supabase.from("progress_logs").insert({
        idea_id: idea.id,
        user_id: userData.user.id,
        metric: "ai_feedback",
        value: null,
        note: "Feedback generated",
      });

      toast({
        title: "Success",
        description: "AI feedback generated successfully!",
      });

      // Refresh the idea list to get the updated feedback
      onIdeaUpdated();
    } catch (error) {
      console.error("Error getting AI feedback:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get AI feedback",
        variant: "destructive",
      });
      setFeedbackDialogIdeaId(null);
    } finally {
      setLoadingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search your ideas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <IdeaCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by title, description, or tags..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Ideas List */}
      {filteredIdeas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {ideas.length === 0
                ? "No ideas yet. Create your first idea above!"
                : "No ideas match your search."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedIdeas.map((idea) => (
              <Card key={idea.id} className="hover:shadow-lg transition-shadow">
                {editingId === idea.id ? (
                  <CardContent className="pt-6 space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor={`edit-title-${idea.id}`}>Title</Label>
                      <Input
                        id={`edit-title-${idea.id}`}
                        value={editForm.title}
                        onChange={(e) =>
                          setEditForm({ ...editForm, title: e.target.value })
                        }
                        disabled={updating}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`edit-desc-${idea.id}`}>Description</Label>
                      <Textarea
                        id={`edit-desc-${idea.id}`}
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm({ ...editForm, description: e.target.value })
                        }
                        disabled={updating}
                        rows={3}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`edit-url-${idea.id}`}>Source URL</Label>
                      <Input
                        id={`edit-url-${idea.id}`}
                        type="url"
                        value={editForm.source_url}
                        onChange={(e) =>
                          setEditForm({ ...editForm, source_url: e.target.value })
                        }
                        disabled={updating}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`edit-tags-${idea.id}`}>Tags</Label>
                      <Input
                        id={`edit-tags-${idea.id}`}
                        value={editForm.tags}
                        onChange={(e) =>
                          setEditForm({ ...editForm, tags: e.target.value })
                        }
                        disabled={updating}
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => handleUpdate(idea.id)}
                        disabled={updating}
                        className="flex-1"
                      >
                        {updating ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Save className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cancelEdit}
                        disabled={updating}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                ) : (
                  <>
                    <CardHeader>
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-lg line-clamp-2">
                            {idea.title}
                          </CardTitle>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDistanceToNow(new Date(idea.created_at), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                      <div className="flex items-center justify-between">
                        {idea.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {idea.tags.map((tag, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <Badge
                          variant="outline"
                          className={`ml-auto ${
                            idea.score >= 80
                              ? "text-green-600 border-green-600"
                              : idea.score >= 60
                              ? "text-yellow-600 border-yellow-600"
                              : "text-orange-600 border-orange-600"
                          }`}
                        >
                          <BarChart className="h-3 w-3 mr-1" />
                          {idea.score}
                        </Badge>
                      </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {idea.description && (
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {idea.description}
                        </p>
                      )}
                      {idea.source_url && (
                        <a
                          href={idea.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View Source
                        </a>
                      )}
                      <div className="flex flex-col gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          variant={idea.ai_feedback ? "secondary" : "default"}
                          onClick={() => handleGetAIFeedback(idea)}
                          disabled={loadingFeedback && feedbackDialogIdeaId === idea.id}
                          className="w-full"
                        >
                          {loadingFeedback && feedbackDialogIdeaId === idea.id ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3 mr-1" />
                              {idea.ai_feedback ? "View" : "Get"} AI Feedback
                            </>
                          )}
                        </Button>
                        <GenerateDeckDialog ideaId={idea.id} ideaTitle={idea.title} />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={idea.is_public ? "outline" : "default"}
                            onClick={() => handleTogglePublic(idea)}
                            disabled={togglingPublic === idea.id}
                            className="flex-1"
                          >
                            {togglingPublic === idea.id ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : idea.is_public ? (
                              <>
                                <GlobeLock className="h-3 w-3 mr-1" />
                                Private
                              </>
                            ) : (
                              <>
                                <Globe className="h-3 w-3 mr-1" />
                                Public
                              </>
                            )}
                          </Button>
                          {idea.is_public && idea.public_slug && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/i/${idea.public_slug}`, "_blank")}
                              title="View public page"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEdit(idea)}
                            className="flex-1"
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteId(idea.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </>
                )}
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages} ({filteredIdeas.length} ideas)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* AI Feedback Dialog */}
      <Dialog open={!!feedbackDialogIdeaId} onOpenChange={(open) => !open && setFeedbackDialogIdeaId(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedIdea?.title}</DialogTitle>
            <DialogDescription>
              {selectedIdea?.description || "No description provided"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedIdea?.ai_feedback ? (
              <>
                <AIFeedbackCard
                  feedback={selectedIdea.ai_feedback}
                  score={selectedIdea.score}
                />
                <ActionPlanSection
                  ideaId={selectedIdea.id}
                  userId={userId}
                  onUpdate={onIdeaUpdated}
                />
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Sparkles className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {loadingFeedback
                      ? "Analyzing your idea with AI..."
                      : "No feedback yet — click 'Get AI Feedback' to analyze your idea."}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Idea</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this idea? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
